import React, { useContext, useEffect, useRef, useState } from "react"
import { Button, Div, Text } from "atomize"
import { isMobile, isTablet } from "react-device-detect"
import Pallino from "../landing/Pallino"
import { theme } from "../settings/config"
import { APP_VERSION, BUILD_COMMIT } from "../settings/version"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { lessonsContext } from "../providers/lessons.provider"
import { useTheme } from "../providers/theme.provider"
import { auth } from "../services/firebase.service"
import { addFeedback, newFeedbackID } from "../utils/firestore.utils"
import { buildFeedbackContext, visitedPages, FEEDBACK_MAX_TEXT } from "../utils/feedback.utils"
import {
	crashDetails,
	crashFeedback,
	crashKey,
	crashNoteFeedback,
	shouldReportCrash,
} from "../utils/crash.utils"
import { isOffline } from "../utils/network.utils"
import { roleOf } from "../utils/school.utils"

// ── Quando l'app si inceppa ──
// Un errore mentre React disegna, senza niente intorno, lasciava la pagina
// bianca: niente tasti, niente parole, e a noi non arrivava niente. Qui lo
// si prende (un "error boundary" di React) e al suo posto c'è una pagina:
// Pallino storto, cosa è successo, cosa è al sicuro, e **Riapri l'app**.
//
// La segnalazione parte da sola appena si vede la pagina (vedi
// src/utils/crash.utils.js). Sotto si può scrivere cosa si stava facendo:
// parte, se c'è, toccando Riapri l'app.
//
// Ce ne sono due (src/index.js): una intorno all'app, dentro i provider,
// che sa chi è il maestro, il circolo e la pagina; e una più in alto, per
// quando a rompersi è un provider. Lì i provider non ci sono, e chi è lo
// dice l'accesso.
class CrashGuard extends React.Component {
	state = { error: null }

	// Dove si è rotto, nei componenti: React lo dice in `componentDidCatch`,
	// che arriva dopo che la pagina è già stata disegnata. Sta qui, e non
	// nello stato, perché la segnalazione parte prima che lo stato si
	// aggiorni: lei lo legge da qui quando parte, e a quel punto c'è.
	caught = { componentStack: null }

	static getDerivedStateFromError(error) {
		return { error }
	}

	componentDidCatch(error, info) {
		this.caught.componentStack = info?.componentStack ?? null
		console.error("L'app si è fermata:", error)
	}

	render() {
		if (!this.state.error) return this.props.children
		return <CrashScreen error={this.state.error} caught={this.caught} />
	}
}

// Chi è: dal profilo se c'è, se no dall'accesso. Senza, la segnalazione non
// può partire: le regole la vogliono a nome di un maestro.
const coachIDFromAuth = async () => {
	try {
		const token = await auth.currentUser?.getIdTokenResult()
		return token?.claims?.coachID ?? null
	} catch {
		return null
	}
}

export function CrashScreen({ error, caught }) {
	const { coach } = useContext(profileContext) ?? {}
	const { schools, selectedSchool } = useContext(schoolContext) ?? {}
	const { selectedDate, lessonsView } = useContext(lessonsContext) ?? {}
	const { mode: themeMode, theme: currentTheme } = useTheme() ?? {}

	const [note, setNote] = useState("")
	const [isLeaving, setLeaving] = useState(false)
	// la segnalazione dell'errore: `{ id, coachID }`, e `id` null se la
	// stessa era già partita poco fa. Senza maestro non parte niente.
	const report = useRef(null)
	const [canReport, setCanReport] = useState(false)

	const buildContext = coachID => {
		const { page, previousPage } = visitedPages()
		return buildFeedbackContext({
			coachID,
			schoolID: coach ? selectedSchool : null,
			role:
				coach && selectedSchool ? roleOf(coach, schools?.[selectedSchool], selectedSchool) : null,
			page: page ?? window.location.pathname,
			previousPage,
			selectedDate,
			lessonsView,
			appVersion: APP_VERSION,
			buildCommit: BUILD_COMMIT,
			device: isTablet ? "tablet" : isMobile ? "phone" : "computer",
			theme: currentTheme,
			themeMode,
			online: !isOffline(),
			userAgent: navigator.userAgent,
			screen: `${window.innerWidth}x${window.innerHeight}`,
		})
	}

	// Parte appena la pagina si vede, una volta sola: chi chiude l'app senza
	// toccare niente ce la manda lo stesso
	useEffect(() => {
		let isCurrent = true
		const send = async () => {
			const coachID = coach?.coachID ?? (await coachIDFromAuth())
			if (!coachID || !isCurrent) return
			const crash = crashDetails(error, caught?.componentStack)
			const context = buildContext(coachID)
			setCanReport(true)
			if (!shouldReportCrash(crashKey(crash, context.page))) {
				report.current = { id: null, coachID }
				return
			}
			const id = newFeedbackID()
			report.current = { id, coachID }
			addFeedback(crashFeedback(crash, context), id).catch(() => {})
		}
		send()
		return () => {
			isCurrent = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error])

	// La riga, se c'è, parte prima di riaprire, senza aspettarla troppo: senza
	// rete resta in coda sul telefono. Si riparte dalle lezioni: la pagina
	// di prima è quella che si è rotta.
	const reopen = async () => {
		setLeaving(true)
		const sent = report.current
		if (sent && note.trim() !== "") {
			try {
				await addFeedback(crashNoteFeedback(note, sent.id, buildContext(sent.coachID)))
			} catch {
				// riaprire conta più della riga
			}
		}
		window.location.assign("/lessons")
	}

	return (
		<Div
			minH="100vh"
			bg={theme.colors.background}
			d="flex"
			flexDir="column"
			align="center"
			justify="center"
			textAlign="center"
			p={{ x: "1.75rem", y: "2.5rem" }}>
			<Div w="100%" maxW="22rem" d="flex" flexDir="column" align="center">
				<Pallino pose="storto" size={112} style={{ marginBottom: "0.875rem" }} />
				<Text
					textSize="subheader"
					textWeight="bold"
					textColor={theme.colors.text}
					m={{ b: "0.375rem" }}>
					Qualcosa si è inceppato
				</Text>
				<Text textColor={theme.colors.text_light} style={{ lineHeight: 1.5 }}>
					Le lezioni e tutto quello che avevi salvato sono al sicuro. Quello che stavi scrivendo
					adesso potrebbe essere da rifare.
				</Text>

				{canReport && (
					<label style={{ width: "100%", marginTop: "1.125rem", textAlign: "left" }}>
						<Text
							tag="span"
							d="block"
							textSize="caption"
							textWeight="600"
							textColor={theme.colors.gunmetal}
							m={{ b: "0.375rem", l: "0.25rem" }}>
							Cosa stavi facendo?{" "}
							<Text tag="span" textWeight="400" textColor={theme.colors.text_light}>
								Facoltativo
							</Text>
						</Text>
						<textarea
							value={note}
							maxLength={FEEDBACK_MAX_TEXT}
							rows={3}
							placeholder="Es. stavo salvando il programma della lezione"
							disabled={isLeaving}
							onChange={event => setNote(event.target.value)}
							style={{
								width: "100%",
								boxSizing: "border-box",
								resize: "none",
								border: 0,
								borderRadius: "15px",
								padding: "0.625rem 0.75rem",
								background: theme.colors.surface,
								font: "inherit",
								fontSize: "0.875rem",
								lineHeight: 1.4,
								color: theme.colors.text,
							}}
						/>
					</label>
				)}

				<Button
					w="100%"
					h="3rem"
					m={{ t: "1.25rem" }}
					bg={theme.colors.primary}
					textColor="white"
					textWeight="600"
					rounded="20px"
					disabled={isLeaving}
					onClick={reopen}>
					{isLeaving ? "Riapro…" : "Riapri l'app"}
				</Button>
				{canReport && (
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.75rem" }}>
						La segnalazione parte comunque, anche senza.
					</Text>
				)}
			</Div>
		</Div>
	)
}

export default CrashGuard
